// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AppIcon } from '@loa/ui-components';
import { getTenant } from '../utils/auth';
import type { BrandingConfig } from '../types/branding';
import { DEFAULT_BRANDING } from '../types/branding';
import { LogoWithBranding } from '../components/LogoWithBranding';
import './LoginPage.css';

/**
 * Shape returned by GET /api/brokerages/subdomain/{subdomain}/login-options.
 * Backed by the resource-server endpoint added alongside Phase 2 (Google SSO)
 * so this login page knows which IdP buttons to render and whether the
 * password form should be shown at all.
 */
interface LoginOptions {
  ssoProviders: { alias: string; displayName: string }[];
  passwordLoginEnabled: boolean;
}

/**
 * Get the return URL from query params, with validation
 */
const getReturnUrl = (searchParams: URLSearchParams): string => {
  const returnUrl = searchParams.get('returnUrl');
  if (returnUrl) {
    try {
      const decoded = decodeURIComponent(returnUrl);
      // Security: Only allow relative URLs starting with /
      if (decoded.startsWith('/') && !decoded.startsWith('//')) {
        return decoded;
      }
    } catch {
      // Invalid URL encoding, ignore
    }
  }
  return '/';
};

export const LoginPage = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [branding, setBranding] = useState<BrandingConfig>(DEFAULT_BRANDING);

  // SSO providers + whether the password form is enabled for this brokerage.
  // Loaded once on mount; while loading we fall back to "password only" so
  // the page never renders empty if the API is slow / down.
  const [loginOptions, setLoginOptions] = useState<LoginOptions>({
    ssoProviders: [],
    passwordLoginEnabled: true,
  });

  // Change-password state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Forgot-password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordSubmitted, setForgotPasswordSubmitted] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState('');

  // Get return URL once on mount
  const returnUrl = getReturnUrl(searchParams);

  useEffect(() => {
    // Load branding configuration using public endpoint (no auth required)
    const loadBranding = async () => {
      try {
        const subdomain = getTenant();
        const response = await fetch(`/api/branding/public/subdomain/${subdomain}`);

        if (!response.ok) {
          throw new Error('Failed to fetch branding');
        }

        const brandingConfig = await response.json();
        setBranding(brandingConfig);

        // Apply custom colors if available
        if (brandingConfig.colors?.primary) {
          document.documentElement.style.setProperty('--primary-color', brandingConfig.colors.primary);
        }
        if (brandingConfig.colors?.secondary) {
          document.documentElement.style.setProperty('--secondary-color', brandingConfig.colors.secondary);
        }
      } catch (err) {
        console.error('Failed to load branding, using defaults:', err);
        setBranding(DEFAULT_BRANDING);
      }
    };

    loadBranding();

    // Fetch login options in parallel. Failure mode: fall back to default
    // (password-only, no SSO buttons) so a flaky API doesn't lock people out.
    const loadLoginOptions = async () => {
      try {
        const subdomain = getTenant();
        const response = await fetch(
          `/api/public/brokerages/subdomain/${subdomain}/login-options`
        );
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const data: LoginOptions = await response.json();
        setLoginOptions({
          ssoProviders: data.ssoProviders ?? [],
          // Default to true if the field is missing — better to show the form
          // than to lock the user out of a brokerage that hasn't configured SSO.
          passwordLoginEnabled: data.passwordLoginEnabled ?? true,
        });
      } catch (err) {
        console.warn('Failed to load login options, falling back to password-only:', err);
        // Keep the defaults from useState — password form visible, no SSO.
      }
    };

    loadLoginOptions();
  }, []);

  const handleSsoLogin = (alias: string) => {
    // advisor-bff exposes /oauth2/authorization/{tenantId} per
    // AuthController.generateLoginUrl. kc_idp_hint pre-selects the IdP so
    // Keycloak doesn't show its IdP picker; the user goes straight to the
    // chosen provider.
    const tenant = getTenant();
    globalThis.location.href =
      `/oauth2/authorization/${tenant}?kc_idp_hint=${encodeURIComponent(alias)}` +
      `&returnUrl=${encodeURIComponent(returnUrl)}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      setError('Please enter your email and password');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (data.authenticated) {
        globalThis.location.href = returnUrl;
      } else {
        switch (data.error) {
          case 'invalid_credentials':
            setError('Invalid email or password');
            break;
          case 'account_disabled':
            setError('Your account has been deactivated. Contact your administrator.');
            break;
          case 'account_locked':
            setError('Your account is temporarily locked due to too many failed attempts. Please try again later.');
            break;
          case 'password_change_required':
            setShowChangePassword(true);
            setError('');
            break;
          default:
            setError(data.errorDescription || 'Login failed. Please try again.');
        }
      }
    } catch {
      setError('Unable to connect to the authentication service. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newPassword || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, currentPassword: password, newPassword }),
      });

      const data = await response.json();

      if (data.authenticated) {
        globalThis.location.href = returnUrl;
      } else if (data.error === 'invalid_credentials') {
        setShowChangePassword(false);
        setNewPassword('');
        setConfirmPassword('');
        setError('Invalid email or password. Please try again.');
      } else {
        setError(data.errorDescription || 'Failed to change password. Please try again.');
      }
    } catch {
      setError('Unable to connect to the authentication service. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!forgotPasswordEmail) {
      setForgotPasswordError('Please enter your email address');
      return;
    }

    setForgotPasswordLoading(true);
    setForgotPasswordError('');

    try {
      const response = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: forgotPasswordEmail }),
      });

      if (response.status === 429) {
        setForgotPasswordError('Too many reset requests. Please try again in a few minutes.');
      } else {
        setForgotPasswordSubmitted(true);
      }
    } catch {
      // Show success even on network error to prevent information leakage
      setForgotPasswordSubmitted(true);
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setShowForgotPassword(false);
    setForgotPasswordSubmitted(false);
    setForgotPasswordError('');
  };

  const renderForgotPasswordForm = () => (
    <div className="login-form-inner">
      <div className="login-header">
        <LogoWithBranding
          logoUrl={branding.logoUrl}
          logoAlt="Platform Logo"
          showPoweredBy={branding.showPoweredByBadge}
        />

        <h1 className="login-heading">Forgot password</h1>
        <p className="login-subtitle">
          Enter the work email address that you signed up with. We will send you an email with a temporary password.
        </p>
      </div>

      {forgotPasswordSubmitted ? (
        <div className="login-form-fields">
          <output className="login-success-message">
            If your email is registered, you will receive an email with a temporary password.
          </output>
          <button
            type="button"
            className="login-back-link"
            onClick={handleBackToLogin}
          >
            Back to sign in
          </button>
        </div>
      ) : (
        <form onSubmit={handleForgotPassword} noValidate>
          <div className="login-form-fields">
            <div className="login-field">
              <label className="login-label" htmlFor="forgot-email">
                Email address<span className="login-label-required">*</span>
              </label>
              <input
                id="forgot-email"
                className="login-input"
                type="email"
                placeholder="Email address"
                value={forgotPasswordEmail}
                onChange={(e) => { setForgotPasswordEmail(e.target.value); setForgotPasswordError(''); }}
                disabled={forgotPasswordLoading}
                autoFocus
                autoComplete="username"
              />
            </div>

            {forgotPasswordError && (
              <div className="login-error" role="alert">
                {forgotPasswordError}
              </div>
            )}

            <button
              type="submit"
              className="login-submit-btn"
              disabled={forgotPasswordLoading}
              aria-busy={forgotPasswordLoading}
            >
              {forgotPasswordLoading ? 'Sending...' : 'Send Link'}
            </button>

            <button
              type="button"
              className="login-back-link"
              onClick={handleBackToLogin}
            >
              Back to sign in
            </button>
          </div>
        </form>
      )}
    </div>
  );

  const renderChangePasswordForm = () => (
    <div className="login-form-inner">
      <div className="login-header">
        <LogoWithBranding
          logoUrl={branding.logoUrl}
          logoAlt="Platform Logo"
          showPoweredBy={branding.showPoweredByBadge}
        />

        <h1 className="login-heading">Set New Password</h1>
        <p className="login-subtitle">Please create a new password for your account.</p>
      </div>

      <form onSubmit={handleChangePassword} noValidate>
        <div className="login-form-fields">
          <div className="login-field">
            <span className="login-label">Email address</span>
            <div className="login-email-display">{email}</div>
          </div>

          <div className="login-field">
            <label className="login-label" htmlFor="new-password">
              New Password<span className="login-label-required">*</span>
            </label>
            <div className="login-password-wrapper">
              <input
                id="new-password"
                className="login-input login-input--password"
                type={showNewPassword ? 'text' : 'password'}
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
                disabled={loading}
                autoFocus
                autoComplete="new-password"
              />
              <button
                type="button"
                className="login-password-toggle"
                onClick={() => setShowNewPassword(!showNewPassword)}
                aria-label={showNewPassword ? 'Hide password' : 'Show password'}
              >
                {showNewPassword ? <AppIcon name="eye slash outline" size={16} /> : <AppIcon name="eye open outline" size={16} />}
              </button>
            </div>
          </div>

          <div className="login-field">
            <label className="login-label" htmlFor="confirm-password">
              Confirm Password<span className="login-label-required">*</span>
            </label>
            <div className="login-password-wrapper">
              <input
                id="confirm-password"
                className="login-input login-input--password"
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                disabled={loading}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="login-password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
              >
                {showConfirmPassword ? <AppIcon name="eye slash outline" size={16} /> : <AppIcon name="eye open outline" size={16} />}
              </button>
            </div>
          </div>

          {error && (
            <div className="login-error" role="alert">
              {error}
            </div>
          )}

          <button
            type="submit"
            className="login-submit-btn"
            disabled={loading}
            aria-busy={loading}
          >
            {loading ? 'Setting password...' : 'Set Password & Sign In'}
          </button>

        </div>
      </form>
    </div>
  );

  const renderLoginForm = () => (
    <div className="login-form-inner">
      <div className="login-header">
        <LogoWithBranding
          logoUrl={branding.logoUrl}
          logoAlt="Platform Logo"
          showPoweredBy={branding.showPoweredByBadge}
        />

        <h1 className="login-heading">{'Welcome  \u{1F44B}'}</h1>
        <p className="login-subtitle">Today is a new day. It&apos;s your day. You shape it.</p>
      </div>

      {loginOptions.ssoProviders.length > 0 && (
        <div className="login-sso-buttons">
          {loginOptions.ssoProviders.map((provider) => (
            <button
              key={provider.alias}
              type="button"
              className={`login-sso-btn login-sso-btn--${provider.alias}`}
              onClick={() => handleSsoLogin(provider.alias)}
              data-testid={`login-sso-${provider.alias}`}
            >
              <AppIcon name={provider.alias} size={20} />
              <span>{provider.displayName}</span>
            </button>
          ))}
          {loginOptions.passwordLoginEnabled && (
            <div className="login-sso-divider">
              <span>or sign in with email</span>
            </div>
          )}
        </div>
      )}

      {!loginOptions.passwordLoginEnabled && loginOptions.ssoProviders.length === 0 && (
        <div className="login-error" role="alert">
          No sign-in methods are enabled for this brokerage. Contact your administrator.
        </div>
      )}

      {loginOptions.passwordLoginEnabled && (
      <form onSubmit={handleSubmit} noValidate>
        <div className="login-form-fields">
          <div className="login-field">
            <label className="login-label" htmlFor="email">
              Email address<span className="login-label-required">*</span>
            </label>
            <input
              id="email"
              className="login-input"
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError(''); }}
              disabled={loading}
              autoFocus
              autoComplete="username"
            />
          </div>

          <div className="login-field-group">
            <div className="login-field">
              <label className="login-label" htmlFor="password">
                Password<span className="login-label-required">*</span>
              </label>
              <div className="login-password-wrapper">
                <input
                  id="password"
                  className="login-input login-input--password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Confirm password"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(''); }}
                  disabled={loading}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="login-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <AppIcon name="eye slash outline" size={16} /> : <AppIcon name="eye open outline" size={16} />}
                </button>
              </div>
            </div>

            <div className="login-forgot-row">
              <button
                type="button"
                className="login-forgot-link"
                onClick={() => {
                  setShowForgotPassword(true);
                  setForgotPasswordEmail(email);
                  setError('');
                }}
              >
                Forgot your password?
              </button>
            </div>
          </div>

          {error && (
            <div className="login-error" role="alert">
              {error}
            </div>
          )}

          <button
            type="submit"
            className="login-submit-btn"
            disabled={loading}
            aria-busy={loading}
          >
            {loading ? 'Signing in...' : 'Login'}
          </button>
        </div>
      </form>
      )}
    </div>
  );

  return (
    <div className="login-page">
      <div className="login-side-panel" aria-hidden="true">
        <div className="login-side-panel-inner">
          <img src="/sideimage.png" alt="" className="login-side-image" />
        </div>
      </div>

      <div className="login-form-container">
        <div className="login-form-scroll">
          {(() => {
            if (showChangePassword) return renderChangePasswordForm();
            if (showForgotPassword) return renderForgotPasswordForm();
            return renderLoginForm();
          })()}
        </div>
      </div>
    </div>
  );
};
